import { Post } from '@/types/post';
import apiClient from './_axios';
import { TableStore } from '@/pages/Admin/Stores';
import { compressImage } from '@/utils/imageCompression';

const API_URL = import.meta.env.VITE_API_URL; // or from '@/constants'

interface StoreResponse {
   result: TableStore[];
}

// export const fetchPosts = async () => {
//   const response = await axios.get(`${API_URL}/posts`);
//   return response.data;
// };

// const token ='eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiI2N2Q5M2RkMmJiYzQ4Yjk1Nzg3ZTZjYzAiLCJpYXQiOjE3NDIyOTM5MzB9.PYeL53rBVU6O8ev1__HyO4Es8sdvD_58Rn38xZ8DRnY';

export async function fetchStores(): Promise<StoreResponse> {
   const response = await apiClient.get<StoreResponse>('/store');
   // console.log(response.data);
   return response.data;
}

interface CreateStoreType {
   storename: string;
   location: string;
}

interface StoreSettingsType {
   btnColor: string;
   btnLabel: string;
   btnEmbed: string;
   designTheme: string;
   colorTheme: string;
   imgs: string[];
   slidesEnabled?: boolean;
   slidesHeight?: number;
   portalIp?: string;
}

interface UpdateStoreType {
   storeId: string;
   storename: string;
   description?: string;
   location: string;
   logo?: string;
   settings: StoreSettingsType;
}

interface UpdateStoreImageType {
   storeId: string;
   images: string[];
}

//  create store
export async function createStore(store: CreateStoreType) {
   return await apiClient.post('/store/create', store);
}

// update store
export async function updateStore(store: UpdateStoreType) {
   return await apiClient.put('/store/update', store);
}

// // update store image
// export async function updateStoreImage(store: UpdateStoreImageType) {
//    return await apiClient.put('/store/update-store-slider', store);
// }

// Generic Cloudinary upload — endpoint name is historical (originally
// the store image-slider feature) but server-side it's unscoped: any
// authenticated user can upload an image and gets back a {imageUrl}.
// Reused by the distributor-application doc fields.
export async function uploadImage(
   image: string | File,
   onProgress?: (percent: number) => void
): Promise<{ imageUrl: string }> {
   const r = await uploadImageSlider(image, onProgress);
   return r.data;
}

/**
 * Upload an image and get back its public URL.
 *
 * Files are compressed to WebP here, before they leave the browser. R2 stores
 * and serves the exact bytes it is given, unlike Cloudinary which re-encoded
 * on delivery — so this is now the only thing keeping uploads small. A string
 * argument is a base64 data URL from an older screen and is passed straight
 * through; only real File objects can be compressed.
 */
export async function uploadImageSlider(
   image: string | File,
   onProgress?: (percent: number) => void
) {
   if (typeof image === 'string') {
      return await apiClient.post('/store/upload-image-slider', { image });
   } else {
      const { file } = await compressImage(image);
      const formData = new FormData();
      formData.append('image', file);

      return await apiClient.post('/store/upload-image-slider', formData, {
         headers: {
            'Content-Type': 'multipart/form-data',
         },
         timeout: 300000,
         onUploadProgress: (progressEvent) => {
            if (progressEvent.total && onProgress) {
               const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
               );
               onProgress(percentCompleted);
            }
         },
      });
   }
}

// Upload a short audio clip (portal customizable-theme SFX) to the R2 media
// bucket. Mirrors uploadImageSlider but hits the audio endpoint and returns
// audioUrl. No client-side re-encoding — audio is already compressed, and
// re-encoding it in the browser would cost quality for almost no bytes.
export async function uploadAudio(
   file: File,
   onProgress?: (percent: number) => void
): Promise<{ audioUrl: string }> {
   const formData = new FormData();
   formData.append('audio', file);
   const r = await apiClient.post('/store/upload-audio', formData, {
      headers: {
         'Content-Type': 'multipart/form-data',
      },
      timeout: 300000,
      onUploadProgress: (progressEvent) => {
         if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
               (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
         }
      },
   });
   return r.data;
}

// delete store
export async function deleteStore(storeId: string) {
   return await apiClient.delete(`/store/${storeId}`);
}

/* ── Portal media library ─────────────────────────────────────────────
   Galleries behind the Advanced portal options (backgrounds / slider
   banners / sounds) + the WiFi Settings Library manager. Files upload
   through uploadImage/uploadAudio; these register/list/remove the URLs.
   global=true assets are the admin-curated library every operator sees. */

export type MediaKind = 'background' | 'slider' | 'sound' | 'video';

/** Sound slot a sound asset is meant for; '' = shows in every slot. */
export type MediaSoundCategory = '' | 'countdown' | 'insert-coin';

export interface MediaAsset {
   _id: string;
   kind: MediaKind;
   url: string;
   name: string;
   category?: MediaSoundCategory;
   global: boolean;
}

export async function listMedia(kind?: MediaKind) {
   const r = await apiClient.get<{ assets: MediaAsset[] }>('/store/media', {
      params: kind ? { kind } : undefined,
   });
   return r.data;
}

export async function registerMedia(payload: {
   kind: MediaKind;
   url: string;
   name?: string;
   category?: MediaSoundCategory;
   /** Admin-only — publish into the global library. */
   global?: boolean;
}) {
   const r = await apiClient.post<{ asset: MediaAsset }>('/store/media', payload);
   return r.data;
}

/** Presign a browser→R2 PUT for a portal background video (≤15MB mp4/webm).
    Video is the one media kind that skips our server entirely — images and
    sounds are small enough to pass through the API, a 15MB loop is not. */
export async function presignVideoUpload(payload: {
   name: string;
   size: number;
   contentType: string;
}) {
   const r = await apiClient.post<{
      uploadUrl: string;
      publicUrl: string;
      key: string;
      cacheControl?: string;
   }>('/store/media/video-upload-url', payload);
   return r.data;
}

/** Full video upload flow: presign → PUT the bytes → register the asset. */
export async function uploadVideoAsset(file: File, opts?: { global?: boolean }) {
   const contentType = file.type === 'video/webm' ? 'video/webm' : 'video/mp4';
   const { uploadUrl, publicUrl, cacheControl } = await presignVideoUpload({
      name: file.name,
      size: file.size,
      contentType,
   });
   // cache-control is part of what the server signed, so it has to go up with
   // the PUT or R2 rejects the whole request on a signature mismatch. The
   // bucket's CORS policy lists it alongside content-type for the same reason.
   const put = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
         'Content-Type': contentType,
         ...(cacheControl ? { 'Cache-Control': cacheControl } : {}),
      },
      body: file,
   });
   if (!put.ok) throw new Error('Video upload failed — please try again');
   const { asset } = await registerMedia({
      kind: 'video',
      url: publicUrl,
      name: file.name.replace(/\.[a-z0-9]+$/i, ''),
      global: opts?.global,
   });
   return asset;
}

export async function updateMedia(
   id: string,
   payload: { category: MediaSoundCategory }
) {
   const r = await apiClient.patch<{ ok: boolean; category: string }>(
      `/store/media/${id}`,
      payload
   );
   return r.data;
}

/* ── Admin per-layout default portal configs ──────────────────────── */
export async function getLayoutDefaults() {
   const r = await apiClient.get<{ defaults: Record<string, unknown> }>(
      '/store/layout-defaults'
   );
   return r.data;
}

export async function saveLayoutDefault(layout: string, config: unknown) {
   const r = await apiClient.put(`/store/layout-defaults/${layout}`, { config });
   return r.data;
}
export async function deleteMedia(id: string) {
   const r = await apiClient.delete<{ ok: boolean }>(`/store/media/${id}`);
   return r.data;
}
